import React, { useState } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { javascript } from '@codemirror/lang-javascript';
import { oneDark } from '@codemirror/theme-one-dark';
import useThemeStore from '../../store/useThemeStore';
import JSZip from 'jszip';

const CodeBlocksEditor = ({ codeBlocks, importStatement }) => {
  const theme = useThemeStore((state) => state.theme);
  const languageExtension = javascript();
  
  // Initialize all sections as expanded (open)
  const [expandedSections, setExpandedSections] = useState(() => {
    const state = {};
    codeBlocks.forEach((block, index) => {
      state[index] = true; // All expanded by default
    });
    return state;
  });
  const [copyAllFeedback, setCopyAllFeedback] = useState(false);

  const toggleSection = (index) => {
    setExpandedSections(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const copyToClipboard = async (code, blockName, e) => {
    try {
      // Prepend import statement if required
      const codeToCopy = code;
      await navigator.clipboard.writeText(codeToCopy);
      
      // Visual feedback
      const btn = e.target.closest('button');
      if (btn) {
        const originalHTML = btn.innerHTML;
        btn.innerHTML = '<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg> Copied!';
        btn.classList.add('bg-green-500', 'text-white');
        setTimeout(() => {
          btn.innerHTML = originalHTML;
          btn.classList.remove('bg-green-500', 'text-white');
        }, 2000);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const copyAllBlocks = async (e) => {
    try {
      // Combine all blocks with their imports
      let allCode = '';
      let importAdded = false;
      
      sortedBlocks.forEach((block, index) => {
        // Add import statement only once at the beginning if any block requires it
        if (block.requiresImport && importStatement && !importAdded) {
          allCode += importStatement + '\n\n';
          importAdded = true;
        }
        
        // Add block code
        allCode += `// MARK: - ${block.name}\n`;
        allCode += block.code;
        
        // Add spacing between blocks (except last one)
        if (index < sortedBlocks.length - 1) {
          allCode += '\n\n';
        }
      });
      
      await navigator.clipboard.writeText(allCode);
      
      // Visual feedback
      setCopyAllFeedback(true);
      setTimeout(() => {
        setCopyAllFeedback(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy all:', err);
    }
  };

  const downloadFile = (content, filename) => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadBlock = (block, index) => {
    const displayCode = block.requiresImport && importStatement 
      ? `${importStatement}\n\n${block.code}` 
      : block.code;
    
    // Create a safe filename from block name
    const safeName = block.name
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .toLowerCase();
    
    const filename = `${safeName}.swift`;
    downloadFile(displayCode, filename);
  };

  const downloadAllBlocks = async () => {
    try {
      const zip = new JSZip();
      
      // Add each block as a separate file in the ZIP
      sortedBlocks.forEach((block) => {
        // Get the code with imports if required
        const displayCode = block.requiresImport && importStatement 
          ? `${importStatement}\n\n${block.code}` 
          : block.code;
        
        // Create a safe filename from block name
        const safeName = block.name
          .replace(/[^a-zA-Z0-9\s]/g, '')
          .replace(/\s+/g, '_')
          .toLowerCase();
        
        const filename = `${safeName}.swift`;
        
        // Add file to ZIP
        zip.file(filename, displayCode);
      });
      
      // Generate ZIP file
      const blob = await zip.generateAsync({ type: 'blob' });
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'swift_code_blocks.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to create ZIP file:', err);
      // Fallback: download files individually if ZIP fails
      sortedBlocks.forEach((block, index) => {
        const displayCode = block.requiresImport && importStatement 
          ? `${importStatement}\n\n${block.code}` 
          : block.code;
        
        const safeName = block.name
          .replace(/[^a-zA-Z0-9\s]/g, '')
          .replace(/\s+/g, '_')
          .toLowerCase();
        
        const filename = `${safeName}.swift`;
        
        setTimeout(() => {
          downloadFile(displayCode, filename);
        }, index * 100);
      });
    }
  };

  // Sort blocks by order if provided
  const sortedBlocks = [...codeBlocks].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-900">
      <div className="bg-white dark:bg-gray-900 px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-300">
            Generated Swift Code
          </h3>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {sortedBlocks.length} {sortedBlocks.length === 1 ? 'block' : 'blocks'}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={copyAllBlocks}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-1.5 ${
              copyAllFeedback
                ? 'bg-green-500 text-white'
                : 'bg-gradient-primary hover:opacity-90 text-white'
            }`}
            title="Copy all blocks to clipboard"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            {copyAllFeedback ? 'Copied!' : 'Copy All'}
          </button>
          <button
            onClick={downloadAllBlocks}
            className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 rounded-md text-white text-xs font-medium transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-1.5"
            title="Download all blocks as .swift files"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Download All
          </button>
        </div>
      </div>
      
      <div className="flex-1 min-h-0 overflow-y-auto">
        {sortedBlocks.map((block, index) => {
          const isExpanded = expandedSections[index] !== false;
          const displayCode = block.requiresImport && importStatement 
            ? `${importStatement}\n\n${block.code}` 
            : block.code;

          return (
            <div
              key={index}
              className="border-b border-gray-200 dark:border-gray-700 last:border-b-0"
            >
              {/* Accordion Header */}
              <div
                className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                onClick={() => toggleSection(index)}
              >
                <div className="flex items-center gap-3 flex-1">
                  <svg
                    className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform ${
                      isExpanded ? 'transform rotate-90' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-300">
                      {block.name}
                    </h4>
                    {block.optional && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        Optional
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      copyToClipboard(displayCode, block.name, e);
                    }}
                    className="px-3 py-1.5 bg-gradient-primary hover:opacity-90 rounded-md text-white text-xs font-medium transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-1.5"
                    title={`Copy ${block.name} to clipboard`}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Copy
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadBlock(block, index);
                    }}
                    className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 rounded-md text-white text-xs font-medium transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-1.5"
                    title={`Download ${block.name} as .swift file`}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Download
                  </button>
                </div>
              </div>

              {/* Accordion Body */}
              {isExpanded && (
                <div className="bg-white dark:bg-gray-900 transition-all duration-200">
                  <CodeMirror
                    value={displayCode}
                    height="auto"
                    minHeight="200px"
                    extensions={[languageExtension]}
                    theme={theme === 'dark' ? oneDark : undefined}
                    editable={false}
                    basicSetup={{
                      lineNumbers: true,
                      foldGutter: true,
                      dropCursor: false,
                      allowMultipleSelections: false,
                    }}
                    className="border-t border-gray-100 dark:border-gray-800"
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CodeBlocksEditor;

